import { useSearchParams } from "react-router";

import { CatalogoDePreciosDeIa } from "@/components/ia/CatalogoDePreciosDeIa";
import { GraficoDeCostoDiario } from "@/components/ia/GraficoDeCostoDiario";
import { LogDeConsumoDeIa } from "@/components/ia/LogDeConsumoDeIa";
import { SelectorDeFecha } from "@/components/SelectorDeFecha";
import { EstadoDeError } from "@/components/estados/EstadoDeError";
import { EstadoSinConexion } from "@/components/estados/EstadoSinConexion";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useResumenDeConsumoDeIa } from "@/hooks/useConsumoDeIa";
import { estadoDeConsulta } from "@/lib/estadoDeConsulta";
import {
  formatearCostoUsd,
  formatearPorcentaje,
  formatearTokens,
} from "@/lib/formatoDeConsumo";
import type { ConsumoPorFuncion, ConsumoPorModelo } from "@/lib/consumoDeIa";

type Seccion = "resumen" | "historial" | "precios";

/**
 * Ordena un desglose por COSTO descendente, no por peticiones —que es como lo
 * entrega el servidor (`IaConsumoController::resumen`, `orderByDesc('requests')`,
 * pensado para el `groupBy`, no para la lectura)—. Es lo que responde
 * directamente a la pregunta que esta pantalla existe para contestar: qué
 * función o qué modelo cuesta más.
 *
 * `null` (sin precio conocido) va AL FINAL, nunca arriba: tratarlo como cero
 * lo pondría por debajo de un costo real de cero, que no es lo mismo — "no lo
 * sé" no es "no costó nada".
 */
function ordenarPorCostoDesc<T extends { costo_usd: string | null }>(filas: T[]): T[] {
  return [...filas].sort((a, b) => {
    const costoA = a.costo_usd === null ? -1 : Number(a.costo_usd);
    const costoB = b.costo_usd === null ? -1 : Number(b.costo_usd);
    return costoB - costoA;
  });
}

/**
 * Cuánto gasta el sistema en IA y en qué (`odd/tasks/consumo-de-ia.md`).
 *
 * TRES SECCIONES Y NO UNA SOLA PANTALLA LARGA: el resumen (KPIs, costo diario,
 * desglose), el log de peticiones y el catálogo de precios responden tres
 * preguntas distintas — "cuánto llevamos", "qué pasó exactamente" y "cuánto
 * cobra cada modelo" — y cada una trae su propio filtro y su propia
 * paginación. Apiladas en una sola pantalla, sus barras de filtro competirían
 * por la misma franja de arriba.
 *
 * SOLO EL RESUMEN SE MONTA SIEMPRE: el log y el catálogo de precios viven en
 * componentes propios que se montan recién cuando su pestaña está activa, así
 * que su consulta no se dispara hasta que alguien la pide — abrir esta
 * pantalla no paga por las tres de una vez.
 */
export function ConsumoDeIa() {
  const [params, setParams] = useSearchParams();
  const seccion = (params.get("seccion") as Seccion | null) ?? "resumen";

  const desdeParam = params.get("desde") ?? "";
  const hastaParam = params.get("hasta") ?? "";
  const resumen = useResumenDeConsumoDeIa({
    desde: desdeParam || undefined,
    hasta: hastaParam || undefined,
  });
  const estado = estadoDeConsulta(resumen);

  function cambiarSeccion(valor: string) {
    const siguiente = new URLSearchParams(params);
    if (valor === "resumen") siguiente.delete("seccion");
    else siguiente.set("seccion", valor);
    setParams(siguiente, { replace: true });
  }

  function cambiarFecha(clave: "desde" | "hasta", iso: string) {
    const siguiente = new URLSearchParams(params);
    if (iso) siguiente.set(clave, iso);
    else siguiente.delete(clave);
    setParams(siguiente, { replace: true });
  }

  // Mientras el resumen no llegó, no hay fecha del servidor que mostrar: el
  // campo queda vacío en vez de inventar un rango. Con la respuesta, si el
  // usuario no eligió nada, se muestra el que el servidor RESOLVIÓ (el mes
  // actual hasta hoy) — así ese cálculo no se duplica acá.
  const desdeMostrado = desdeParam || resumen.data?.desde || "";
  const hastaMostrado = hastaParam || resumen.data?.hasta || "";

  const totales = resumen.data?.totales;
  const porDia = resumen.data?.por_dia ?? [];
  const porFuncion = ordenarPorCostoDesc(resumen.data?.por_funcion ?? []);
  const porModelo = ordenarPorCostoDesc(resumen.data?.por_modelo ?? []);
  const requestsSinPrecio = totales?.requests_sin_precio ?? 0;

  return (
    <div className="space-y-5 px-6 pt-2 pb-6 lg:px-8 lg:pb-8">
      <p className="text-ink-muted max-w-[70ch] text-sm">
        Cuánto gasta el sistema en llamadas a un proveedor de IA, y en qué. Los costos de una
        petición pasada nunca cambian: un precio nuevo solo rige desde que se carga.
      </p>

      <Tabs variant="underline" value={seccion} onValueChange={cambiarSeccion}>
        <TabsList>
          <TabsTrigger value="resumen">Resumen</TabsTrigger>
          <TabsTrigger value="historial">Historial de peticiones</TabsTrigger>
          <TabsTrigger value="precios">Catálogo de precios</TabsTrigger>
        </TabsList>
      </Tabs>

      {seccion === "resumen" ? (
        <div className="space-y-5">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex w-44 flex-col gap-1.5">
              <Label htmlFor="consumo-desde">Desde</Label>
              <SelectorDeFecha
                id="consumo-desde"
                value={desdeMostrado}
                onChange={(iso) => cambiarFecha("desde", iso)}
              />
            </div>
            <div className="flex w-44 flex-col gap-1.5">
              <Label htmlFor="consumo-hasta">Hasta</Label>
              <SelectorDeFecha
                id="consumo-hasta"
                value={hastaMostrado}
                onChange={(iso) => cambiarFecha("hasta", iso)}
              />
            </div>
          </div>

          {estado.sinConexion ? (
            <EstadoSinConexion que="el resumen de consumo de IA" />
          ) : estado.hayError ? (
            <EstadoDeError
              titulo="No pudimos cargar el resumen"
              error={resumen.error}
              onReintentar={() => resumen.refetch()}
              reintentando={estado.recargando}
            />
          ) : estado.cargando ? (
            <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-24 w-full rounded-xl" />
              ))}
            </div>
          ) : (
            <>
              {requestsSinPrecio > 0 ? (
                <div
                  role="alert"
                  className="bg-warning-soft text-warning-soft-foreground flex flex-wrap items-center justify-between gap-3 rounded-xl px-4 py-3 text-sm"
                >
                  <span>
                    {requestsSinPrecio === 1
                      ? "1 petición sin precio registrado para su modelo"
                      : `${formatearTokens(requestsSinPrecio)} peticiones sin precio registrado para sus modelos`}{" "}
                    — el costo total de arriba no las incluye, así que está por debajo del gasto
                    real.
                  </span>
                  <button
                    type="button"
                    className="font-bold underline underline-offset-2"
                    onClick={() => cambiarSeccion("precios")}
                  >
                    Ver catálogo de precios
                  </button>
                </div>
              ) : null}

              <div className="grid grid-cols-2 gap-4 xl:grid-cols-4">
                <KpiTile etiqueta="Costo total" valor={formatearCostoUsd(totales?.costo_usd ?? null)} />
                <KpiTile etiqueta="Peticiones" valor={formatearTokens(totales?.requests ?? 0)} />
                <KpiTile
                  etiqueta="Tokens de entrada"
                  valor={formatearTokens(totales?.tokens_entrada ?? 0)}
                />
                <KpiTile
                  etiqueta="Tokens de salida"
                  valor={formatearTokens(totales?.tokens_salida ?? 0)}
                />
              </div>

              <p className="text-ink-hint text-xs">
                Caché leída: {formatearTokens(totales?.tokens_cache_lectura ?? 0)} · Caché escrita:{" "}
                {formatearTokens(totales?.tokens_cache_escritura ?? 0)} · Razonamiento:{" "}
                {formatearTokens(totales?.tokens_razonamiento ?? 0)} tokens
              </p>

              <section className="bg-card space-y-3 rounded-xl border p-5">
                <h3 className="text-h3 text-ink">Costo diario</h3>
                <GraficoDeCostoDiario datos={porDia} />
              </section>

              <div className="grid gap-5 lg:grid-cols-2">
                <TablaDeDesglose
                  titulo="Costo por función"
                  columnaNombre="Función"
                  filas={porFuncion.map((f) => ({ id: f.ia_funcion_id ?? f.nombre, nombre: f.nombre, fila: f }))}
                  costoTotal={totales?.costo_usd ?? null}
                />
                <TablaDeDesgloseModelo filas={porModelo} costoTotal={totales?.costo_usd ?? null} />
              </div>
            </>
          )}
        </div>
      ) : null}

      {seccion === "historial" ? <LogDeConsumoDeIa /> : null}
      {seccion === "precios" ? <CatalogoDePreciosDeIa /> : null}
    </div>
  );
}

function KpiTile({ etiqueta, valor }: { etiqueta: string; valor: string }) {
  return (
    <div className="bg-card rounded-xl border p-5">
      <p className="text-ink-hint text-xs font-semibold">{etiqueta}</p>
      <p className="text-2xl font-extrabold tracking-tight">{valor}</p>
    </div>
  );
}

/** El desglose por función, con nombre + peticiones + tokens + costo + %. */
function TablaDeDesglose({
  titulo,
  columnaNombre,
  filas,
  costoTotal,
}: {
  titulo: string;
  columnaNombre: string;
  filas: { id: string | number; nombre: string; fila: ConsumoPorFuncion }[];
  costoTotal: string | null;
}) {
  return (
    <section className="bg-card space-y-3 rounded-xl border p-5">
      <h3 className="text-h3 text-ink">{titulo}</h3>
      {filas.length === 0 ? (
        <p className="text-ink-hint text-sm">Sin actividad en el período.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{columnaNombre}</TableHead>
                <TableHead className="text-right">Peticiones</TableHead>
                <TableHead className="text-right">Entrada</TableHead>
                <TableHead className="text-right">Salida</TableHead>
                <TableHead className="text-right">Costo</TableHead>
                <TableHead className="text-right">% del costo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas.map(({ id, nombre, fila }) => (
                <TableRow key={id}>
                  <TableCell>{nombre}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(fila.requests)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(fila.tokens_entrada)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(fila.tokens_salida)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs font-bold">
                    {formatearCostoUsd(fila.costo_usd)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearPorcentaje(fila.costo_usd, costoTotal)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}

/** Gemela de `TablaDeDesglose`, con dos columnas de identidad (proveedor +
    modelo) en vez de una — la fila del catálogo no es la misma forma. */
function TablaDeDesgloseModelo({
  filas,
  costoTotal,
}: {
  filas: ConsumoPorModelo[];
  costoTotal: string | null;
}) {
  return (
    <section className="bg-card space-y-3 rounded-xl border p-5">
      <h3 className="text-h3 text-ink">Costo por modelo</h3>
      {filas.length === 0 ? (
        <p className="text-ink-hint text-sm">Sin actividad en el período.</p>
      ) : (
        <div className="overflow-hidden rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Proveedor</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead className="text-right">Peticiones</TableHead>
                <TableHead className="text-right">Entrada</TableHead>
                <TableHead className="text-right">Salida</TableHead>
                <TableHead className="text-right">Costo</TableHead>
                <TableHead className="text-right">% del costo</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filas.map((m) => (
                <TableRow key={`${m.proveedor}-${m.modelo}`}>
                  <TableCell>{m.proveedor}</TableCell>
                  <TableCell className="font-mono text-xs">{m.modelo}</TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(m.requests)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(m.tokens_entrada)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearTokens(m.tokens_salida)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs font-bold">
                    {formatearCostoUsd(m.costo_usd)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-xs">
                    {formatearPorcentaje(m.costo_usd, costoTotal)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
